import { defineComponent as _defineComponent } from 'vue'
import { createVNode as _createVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, resolveComponent as _resolveComponent, renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, createBlock as _createBlock, unref as _unref, createElementVNode as _createElementVNode, createCommentVNode as _createCommentVNode, withModifiers as _withModifiers } from "vue"

const _hoisted_1 = { class: "d-flex flex-column align-items-start w-100" }
const _hoisted_2 = {
  class: "mt-4",
  style: {"width":"100%","overflow":"auto"}
}

import moment from "moment";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import { useRouter } from "vue-router";
import { useStore } from "vuex";
import ReportType from "@/core/enums/ReportTypes.enums";
import { formatMonthQuery } from "@/core/helpers/bao-cao-pdf-params";
import { openBaoCaoPdf } from "@/components/shared/pdf-viewer/open-bao-cao-pdf";
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import { swalErrNotification } from "@/core/helpers/utils";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import { useCreatingForm } from "@/core/hooks/use-creating-form";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import { useLoading } from "@/core/hooks/use-loading";
import useTaiKhoan from "@/core/hooks/useTaiKhoan";
import { BaoCaoService } from "@/core/services/BaoCao.service";
import { KhoService } from "@/core/services/Kho.service";
import {
  IHoaDonNhapKhoResponseBase,
  IKhoResponseBase,
  ITaiKhoanTheoHopDongResponseBase,
} from "@/core/interfaces/ApiResponses";
import BangKeChiTietNhapPrintModal from "./print-modals/BangKeChiTietNhapPrintModal.vue";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import ChiTietNhapTable from "./table-components/ChiTietNhapTable.vue";
import DivCol from "@/layout/_shared/DivCol.vue";
import DivRow from "@/layout/_shared/DivRow.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import PageContentSection from "@/layout/_shared/PageContentSection.vue";
import ResponsiveLayout from "@/layout/_shared/ResponsiveLayout.vue";
import TaiKhoanSelectionV2 from "@/components/shared/tai-khoan-selection/TaiKhoanSelectionV2.vue";
import useFormInput from "@/core/hooks/use-form-input";


export default /*#__PURE__*/_defineComponent({
  __name: 'BangKeChiTietNhap',
  setup(__props) {

const { disableDate, defaultDate } = useDateTime();

setCurrentPageTitle("Bảng kê chi tiết nhập");

setBreadcrumbLinks(
  [
    {
      text: "Báo cáo",
    },
    {
      text: "Bảng kê chi tiết nhập",
    },
  ],
  true
);

document.title = "Bảng kê chi tiết nhập - Đường sắt Hà Nội";

const { startLoading, endLoading } = useLoading();

const { formRef } = useCreatingForm();

const { ButtonsType, ButtonTypeColors } = useButtonCustom();

const { onSelectTaiKhoan, clearTaiKhoanData, tkData } = useTaiKhoan();

const store = useStore();
const router = useRouter();

const currentYear = computed(() => store.getters.getSelectedYear);

const form = ref({
  from: defaultDate.value,
  to: defaultDate.value,
  khoId: -1,
  ma_chi_tiet: "",
});

const fromDateValidation = (_, value, callback) => {
  if (!value) {
    callback(new Error("Bắt buộc phải nhập"));
  }

  if (value && form.value.to && moment(value).isAfter(form.value.to)) {
    callback(new Error("Ngày bắt đầu phải trước ngày kết thúc"));
  }

  callback();
};

const toDateValidation = (_, value, callback) => {
  if (!value) {
    callback(new Error("Bắt buộc phải nhập"));
  }

  if (value && form.value.from && moment(value).isBefore(form.value.from)) {
    callback(new Error("Ngày bắt đầu phải trước ngày kết thúc"));
  }

  callback();
};

const khoItems = ref<IKhoResponseBase[]>([]);

const fetchKhoItems = async () => {
  startLoading();

  try {
    const {
      data: {
        data: { data },
      },
    } = await KhoService.fetchAllWarehouses();

    khoItems.value = data;

    endLoading();
  } catch (e) {
    endLoading();

    swalErrNotification(e, "Có lỗi khi lấy danh sách kho");
  }
};

onMounted(() => {
  nextTick(async () => {
    await fetchKhoItems();
  });
});

const isFiltersChanging = ref(true);

const { focusFirstErrInputV2 } = useFormInput();

const rules = ref({
  from: [
    {
      validator: fromDateValidation,
      trigger: "change",
    },
  ],
  to: [
    {
      validator: toDateValidation,
      trigger: "change",
    },
  ],
  khoId: [
    {
      type: "number",
      message: "Phải chọn kho",
      trigger: "change",
      min: 1,
    },
  ],
  ma_chi_tiet: [
    {
      type: "string",
      required: true,
      message: "Bắt buộc phải chọn tài khoản",
      trigger: "change",
    },
  ],
});

const selectingKho = computed(() => {
  if (!form.value.khoId) return null;

  return khoItems.value.find(({ id }) => id === form.value.khoId);
});

watch(
  form,
  () => {
    isFiltersChanging.value = true;
  },
  {
    deep: true,
    immediate: true,
  }
);

const hoaDonNhapKhoItems = ref<IHoaDonNhapKhoResponseBase[]>([]);

const generateBaoCao = () => {
  if (!formRef.value) return;

  formRef.value.validate(async (isValid) => {
    if (isValid) {
      const selectedTaiKhoan = taiKhoanSelected.value;
      if (!selectedTaiKhoan) return;

      try {
        await startLoading();

        const {
          data: { data },
        } = await BaoCaoService.getBangKeChiTietNhap({
          from: form.value.from.getMonth() + 1,
          to: form.value.to.getMonth() + 1,
          idKho: selectingKho.value?.id ?? 0,
          mact: selectedTaiKhoan.ma_ct,
          matk: selectedTaiKhoan.ma_tk,
        });

        await endLoading();

        hoaDonNhapKhoItems.value = data;

        isFiltersChanging.value = false;
      } catch (e) {
        endLoading();

        swalErrNotification(e, "Có lỗi xảy ra");
      }
    } else {
      await focusFirstErrInputV2();

      return;
    }
  });
};

const bangKeChiTietNhapPrintModalRef = ref();

const openPdfViewer = () => {
  if (!taiKhoanSelected.value) return;

  openBaoCaoPdf(router, {
    reportType: ReportType.BANG_KE_CHI_TIET_NHAP,
    from: formatMonthQuery(form.value.from),
    to: formatMonthQuery(form.value.to),
    khoId: form.value.khoId,
    matk: taiKhoanSelected.value.ma_tk,
    mact: taiKhoanSelected.value.ma_ct,
    tenDoiTac: taiKhoanSelected.value.ten_doi_tac,
  });
};

const showPrintButton = computed(() => {
  if (isFiltersChanging.value) return false;

  if (!hoaDonNhapKhoItems.value.length) return false;

  if (
    !selectingKho.value ||
    !form.value.from ||
    !form.value.to ||
    !taiKhoanSelected.value?.ma_ct
  ) {
    return false;
  }

  return true;
});

const taiKhoanSelected = computed<ITaiKhoanTheoHopDongResponseBase | null>(
  () => {
    if (!tkData.value.maChiTiet || !tkData.value.maTaiKhoan) {
      return null;
    }

    return {
      ma_ct: tkData.value.maChiTiet,
      ma_tk: tkData.value.maTaiKhoan,
      ma_so_thue: "",
      ten_doi_tac: tkData.value.tenTaiKhoan,
    };
  }
);

const handleSelectTaiKhoan = (
  payload: Parameters<typeof onSelectTaiKhoan>[0]
) => {
  onSelectTaiKhoan(payload);
  form.value.ma_chi_tiet = tkData.value.maChiTiet || "";
};

const handleClearTaiKhoan = async (type: "tai_khoan" | "chi_tiet") => {
  await clearTaiKhoanData(type);
  form.value.ma_chi_tiet = tkData.value.maChiTiet || "";
};

return (_ctx: any,_cache: any) => {
  const _component_el_option = _resolveComponent("el-option")!
  const _component_el_select = _resolveComponent("el-select")!
  const _component_el_form_item = _resolveComponent("el-form-item")!
  const _component_el_date_picker = _resolveComponent("el-date-picker")!
  const _component_el_form = _resolveComponent("el-form")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    _createVNode(BangKeChiTietNhapPrintModal, {
      ref_key: "bangKeChiTietNhapPrintModalRef",
      ref: bangKeChiTietNhapPrintModalRef,
      from: new Date(form.value.from) ?? null,
      to: new Date(form.value.to) ?? null,
      items: hoaDonNhapKhoItems.value,
      kho: selectingKho.value ?? null,
      "tai-khoan-data": taiKhoanSelected.value,
      onClose: _cache[0] || (_cache[0] = ($event: any) => (bangKeChiTietNhapPrintModalRef.value.isOpen = false))
    }, null, 8, ["from", "to", "items", "kho", "tai-khoan-data"]),
    _createVNode(PageContentBase, { plain: "" }, {
      default: _withCtx(() => [
        _createVNode(PageContentSection, { "always-expanded": true }, {
          header: _withCtx(() => [
            _createTextVNode(" Bảng kê chi tiết nhập ")
          ]),
          default: _withCtx(() => [
            _createVNode(_component_el_form, {
              ref_key: "formRef",
              ref: formRef,
              model: form.value,
              rules: rules.value,
              onSubmit: _withModifiers(generateBaoCao, ["prevent"])
            }, {
              default: _withCtx(() => [
                _createVNode(ResponsiveLayout, null, {
                  "col-1": _withCtx(() => [
                    _createVNode(LabelCustom, {
                      "is-text-muted": false,
                      "is-required": true
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(" Kho: ")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-2": _withCtx(() => [
                    _createVNode(_component_el_form_item, {
                      prop: "khoId",
                      class: "mb-0"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_select, {
                          modelValue: form.value.khoId,
                          "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((form.value.khoId) = $event)),
                          class: "w-100",
                          name: "khoId"
                        }, {
                          default: _withCtx(() => [
                            _createVNode(_component_el_option, {
                              label: "Chưa chọn",
                              value: -1
                            }),
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(khoItems.value, (kho) => {
                              return (_openBlock(), _createBlock(_component_el_option, {
                                key: kho.id,
                                label: kho.ten_kho,
                                value: kho.id
                              }, null, 8, ["label", "value"]))
                            }), 128))
                          ]),
                          _: 1
                        }, 8, ["modelValue"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(ResponsiveLayout, null, {
                  "col-1": _withCtx(() => [
                    _createVNode(LabelCustom, {
                      "is-text-muted": false,
                      "is-required": true
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode("Từ tháng:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-2": _withCtx(() => [
                    _createVNode(_component_el_form_item, {
                      prop: "from",
                      class: "mb-0"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_date_picker, {
                          modelValue: form.value.from,
                          "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((form.value.from) = $event)),
                          class: "w-100",
                          "disabled-date": (time) => _unref(disableDate)(time, currentYear.value, true),
                          type: "month",
                          placeholder: "Từ",
                          name: "from",
                          format: "MM/YYYY"
                        }, null, 8, ["modelValue", "disabled-date"])
                      ]),
                      _: 1
                    })
                  ]),
                  "col-3": _withCtx(() => [
                    _createVNode(LabelCustom, {
                      "is-text-muted": false,
                      "is-required": true
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(" Đến tháng: ")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-4": _withCtx(() => [
                    _createVNode(_component_el_form_item, {
                      prop: "to",
                      class: "mb-0"
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_date_picker, {
                          modelValue: form.value.to,
                          "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((form.value.to) = $event)),
                          class: "w-100",
                          "disabled-date": (time) => _unref(disableDate)(time, currentYear.value, true),
                          placeholder: "Đến",
                          name: "to",
                          format: "MM/YYYY",
                          type: "month"
                        }, null, 8, ["modelValue", "disabled-date"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(_component_el_form_item, {
                  class: "mb-0",
                  prop: "ma_chi_tiet"
                }, {
                  default: _withCtx(() => [
                    _createElementVNode("div", _hoisted_1, [
                      _createVNode(LabelCustom, null, {
                        default: _withCtx(() => [
                          _createTextVNode("Tài khoản")
                        ]),
                        _: 1
                      }),
                      _createVNode(TaiKhoanSelectionV2, {
                        class: "w-100",
                        "ma-tai-khoan-prop": 
                _unref(tkData).maTaiKhoan ? parseInt(_unref(tkData).maTaiKhoan) : undefined
              ,
                        "ma-chi-tiet-prop": _unref(tkData).maChiTiet ? _unref(tkData).maChiTiet : undefined,
                        onOnSelect: handleSelectTaiKhoan,
                        onOnClear: handleClearTaiKhoan
                      }, null, 8, ["ma-tai-khoan-prop", "ma-chi-tiet-prop"])
                    ])
                  ]),
                  _: 1
                }),
                _createVNode(DivRow, null, {
                  default: _withCtx(() => [
                    _createVNode(DivCol, { cols: 8 }),
                    _createVNode(DivCol, {
                      cols: 4,
                      class: "d-flex justify-content-end"
                    }, {
                      default: _withCtx(() => [
                        (showPrintButton.value)
                          ? (_openBlock(), _createBlock(ButtonCustom, {
                              key: 0,
                              "button-type": _unref(ButtonsType).PRINT,
                              onClick: openPdfViewer
                            }, {
                              "button-label": _withCtx(() => [
                                _createTextVNode("In phiếu")
                              ]),
                              _: 1
                            }, 8, ["button-type"]))
                          : _createCommentVNode("", true),
                        _createVNode(ButtonCustom, {
                          "button-type": _unref(ButtonsType).FA_FILE_EXPORT_THIN,
                          "color-type": _unref(ButtonTypeColors).SUCCESS,
                          "tooltip-message": "Xuất báo cáo",
                          onClick: generateBaoCao
                        }, null, 8, ["button-type", "color-type"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                })
              ]),
              _: 1
            }, 8, ["model", "rules"]),
            _createElementVNode("div", _hoisted_2, [
              (!isFiltersChanging.value)
                ? (_openBlock(), _createBlock(ChiTietNhapTable, {
                    key: 0,
                    "is-print-modal": false,
                    items: hoaDonNhapKhoItems.value,
                    stripe: true
                  }, null, 8, ["items"]))
                : _createCommentVNode("", true)
            ])
          ]),
          _: 1
        })
      ]),
      _: 1
    })
  ], 64))
}
}

})